"use client"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/Button"
import { DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import Spinner from "@/components/ui/Spinner"
import { Textarea } from "@/components/ui/textarea"
import { AlertCircle, Send } from "lucide-react"
import React from "react"
import { toast } from "sonner"

interface ContactUsClientProps {
  onClose?: () => void
}

export default function ContactUsClient({
  onClose,
}: ContactUsClientProps = {}) {
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    contactInternalId: "",
  })
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submitStatus, setSubmitStatus] = React.useState<{
    type: "success" | "error" | null
    message: string
  }>({ type: null, message: "" })

  // Load user info and contact info from provider
  React.useEffect(() => {
    let cancelled = false
    const loadUserInfo = async () => {
      try {
        // Get user info
        const res = await fetch("/api/me", { cache: "no-store" })
        const json = res.ok
          ? ((await res.json()) as {
              user?: {
                given_name?: string
                family_name?: string
                email?: string
                mail?: string
              } | null
            })
          : { user: null }
        const user = json?.user || null
        const given = (user?.given_name as string) || ""
        const family = (user?.family_name as string) || ""
        const email = (
          ((user?.email as string) || (user?.mail as string) || "").toLowerCase()
        )
        const name = [given, family].filter(Boolean).join(" ").trim()

        // Try to get contact info for edit link
        let contactInternalId = ""
        if (email) {
          try {
            const contactRes = await fetch(
              `/api/contact-by-email?email=${encodeURIComponent(email)}`
            )
            if (contactRes.ok) {
              const contactData = await contactRes.json()
              const contact = contactData?.node
              if (contact) {
                contactInternalId = String(
                  (contact as { drupal_internal__nid?: number })
                    ?.drupal_internal__nid || ""
                )
              }
            }
          } catch {
            // Ignore contact fetch errors
          }
        }

        if (!cancelled) {
          setFormData((prev) => ({
            ...prev,
            name: name || email.split("@")[0] || "",
            email: email,
            contactInternalId: contactInternalId,
          }))
        }
      } catch (error) {
        console.error("Failed to load user info:", error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadUserInfo()
    return () => {
      cancelled = true
    }
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: "" })

    try {
      const response = await fetch("/api/contactus", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (response.ok) {
        toast.success("Thank you for your message!", {
          description: "We will get back to you soon.",
        })
        // Reset form (but keep name and email)
        setFormData((prev) => ({ ...prev, subject: "", message: "" }))
        // Close dialog after short delay
        setTimeout(() => {
          if (onClose) onClose()
        }, 500)
      } else {
        setSubmitStatus({
          type: "error",
          message: data.error || "Failed to send message. Please try again.",
        })
      }
    } catch {
      setSubmitStatus({
        type: "error",
        message: "An unexpected error occurred. Please try again later.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-2xl py-8 px-4 flex justify-center">
        <Spinner />
      </div>
    )
  }

  const content = (
    <>
      {submitStatus.type === "error" && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{submitStatus.message}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            value={formData.name}
            readOnly
            className="bg-muted cursor-not-allowed"
            placeholder="Your name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            readOnly
            className="bg-muted cursor-not-allowed"
            placeholder="your.email@example.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="subject">Subject *</Label>
          <Input
            id="subject"
            name="subject"
            type="text"
            required
            value={formData.subject}
            onChange={handleChange}
            placeholder="What is this about?"
            disabled={isSubmitting}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Message *</Label>
          <Textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us more about your inquiry..."
            rows={6}
            disabled={isSubmitting}
          />
        </div>

        <Button type="submit" disabled={isSubmitting} className="w-full" size="lg">
          {isSubmitting ? (
            <>
              <span className="mr-2">Sending...</span>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send Message
            </>
          )}
        </Button>
      </form>
    </>
  )

  return (
    <>
      <DialogTitle className="sr-only">Contact Us</DialogTitle>
      <div className="max-w-2xl space-y-2">
        <h3 className="flex items-center gap-2">
          <Send className="size-8" />
          Contact Us
        </h3>
        <p className="text-sm text-muted-foreground">
          Have a question or need assistance? Fill out the form below and
          we&apos;ll get back to you as soon as possible.
        </p>

        {content}
      </div>
    </>
  )
}

